/**
 * WaitWatchFlow — replaces SmartAddFlow.
 *
 * Guest tells us their target eat time; we promise ONLY to text them when
 * it's a good time to leave home. No seat is held, no auto-add happens.
 * If they want to actually join the waitlist without arriving in person,
 * they reply LIST to the SMS we send. The AI receptionist uses the same
 * flow via the PRIORITY LIST trigger.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Bell } from "@phosphor-icons/react";
import { api } from "../lib/api";

export default function WaitWatchFlow({ slug, bizName }) {
  const [form, setForm] = useState({ name: "", phone: "", party_size: 2, desired_eat_time: "", tcpa_consent: false });
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tcpa_consent) return toast.error("Please agree to receive the heads-up text.");
    setBusy(true);
    try {
      // Existing endpoint reused — semantics changed under the hood (worker
      // now sends a "leave now" SMS instead of silently promoting).
      const now = new Date();
      const [hh, mm] = (form.desired_eat_time || "").split(":");
      const target = new Date(now); target.setHours(parseInt(hh || 19), parseInt(mm || 0), 0, 0);
      const r = await api.post(`/public/smart-add/${slug}`, {
        name: form.name,
        phone: form.phone,
        party_size: parseInt(form.party_size),
        desired_eat_time: target.toISOString(),
        tcpa_consent: true,
      });
      setConfirmed(r.data);
      toast.success("Wait Watch set — we'll text you when it's a good time to leave.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set Wait Watch");
    }
    setBusy(false);
  };

  if (confirmed) {
    return (
      <div className="paper-card p-6 border-l-4 border-[#1E3A2F]" data-testid="wait-watch-confirmed">
        <div className="overline mb-1 flex items-center gap-2"><Bell size={14} color="#1E3A2F" weight="fill" /> Wait Watch set</div>
        <h2 className="text-2xl mb-2">Got it, {confirmed.name}!</h2>
        <p className="text-sm text-[#5C6B61] leading-relaxed">
          We'll text {confirmed.phone} when the live wait matches your target — that's the moment to leave home so you sit down close to your window. <b>No table is held for you.</b> When the text arrives, either walk in and check in like anyone else, or reply <b>LIST</b> to hop on the waitlist without needing to arrive first. Reply <b>STOP</b> anytime to cancel.
        </p>
      </div>
    );
  }

  return (
    <div className="paper-card p-6" data-testid="wait-watch-flow">
      <div className="overline mb-1 flex items-center gap-2"><Bell size={14} color="#1E3A2F" weight="fill" /> Wait Watch · we'll text you at the right time</div>
      <h2 className="text-2xl mb-2">Want to eat around a specific time?</h2>
      <p className="text-sm text-[#5C6B61] mb-4 leading-relaxed">
        {bizName} is walk-in only, but you don't have to guess when to leave. Tell us your target eat time and we'll text you when the live wait matches — the moment to head over. <b>We never hold a table for you</b> and this isn't a reservation. When the text arrives you can walk in, or reply <b>LIST</b> to hop on the waitlist remotely.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="overline block mb-1">Your name</label>
          <input required className="input-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ww-name" />
        </div>
        <div>
          <label className="overline block mb-1">Mobile phone</label>
          <input required type="tel" className="input-field" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 555 000 0000" data-testid="ww-phone" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="overline block mb-1">Party size</label>
            <input required type="number" min={1} max={8} className="input-field" value={form.party_size} onChange={(e) => setForm({ ...form, party_size: e.target.value })} data-testid="ww-party" />
          </div>
          <div>
            <label className="overline block mb-1">Target eat time</label>
            <input required type="time" className="input-field" value={form.desired_eat_time} onChange={(e) => setForm({ ...form, desired_eat_time: e.target.value })} data-testid="ww-time" />
          </div>
        </div>
        <label className="flex items-start gap-2 text-xs text-[#5C6B61]">
          <input type="checkbox" className="mt-0.5" checked={form.tcpa_consent} onChange={(e) => setForm({ ...form, tcpa_consent: e.target.checked })} data-testid="ww-consent" />
          <span>I agree to receive one heads-up SMS from {bizName} about my target eat time. Msg &amp; data rates may apply. Reply <b>STOP</b> to cancel.</span>
        </label>
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="ww-submit">
          {busy ? "Setting…" : "Set Wait Watch"}
        </button>
      </form>
    </div>
  );
}
